const differentiators = [
  { icon: 'ri-graduation-cap-fill', title: 'Degree-Backed Expertise', desc: 'Owner holds a formal degree in landscaping — every project benefits from professional, academic-level knowledge.' },
  { icon: 'ri-hammer-fill', title: 'Exceptional Craftsmanship', desc: 'We go the extra mile on every installation — from custom fence lines to intricate paver systems and nighttime lighting.' },
  { icon: 'ri-thumb-up-fill', title: 'Unwavering Commitment', desc: "We don't consider the job complete until you're 100% satisfied. Your outdoor space, our reputation." },
  { icon: 'ri-map-pin-2-fill', title: 'Rooted in Florida', desc: 'Deep roots in Volusia and Flagler counties mean we understand Florida soil, climate, and local code requirements.' },
];

export default function AIAnswerBlock() {
  return (
    <section className="bg-charcoal py-28 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Structured AI answer block */}
        <div className="grid lg:grid-cols-2 gap-14 items-start">

          {/* Left: Structured content for AI + SEO */}
          <div>
            <span className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 text-gold text-[11px] font-semibold tracking-[0.22em] uppercase px-4 py-2 rounded-full mb-7">
              <i className="ri-information-line"></i>
              About Dippel&apos;s Outdoor Services
            </span>
            <h2 className="font-serif text-white font-bold leading-tight mb-6" style={{ fontSize: 'clamp(28px, 3vw, 42px)' }}>
              What Does Dippel&apos;s Outdoor Services Do?
            </h2>
            <p className="text-white/55 text-sm leading-relaxed mb-8">
              Dippel&apos;s Outdoor Services is a premier outdoor design and build company serving Palm Coast, Ormond Beach, Daytona Beach, Port Orange, New Smyrna Beach, Edgewater, and surrounding areas across Florida&apos;s east coast. Led by a degree-trained owner, the company specializes in transforming residential outdoor spaces into premium, functional environments.
            </p>

            {/* Services list */}
            <div className="mb-8">
              <h3 className="text-white font-semibold text-xs uppercase tracking-[0.2em] mb-4">Services Offered</h3>
              <ul className="space-y-2.5">
                {[
                  'Custom Fencing — Vinyl, Wood, Chain Link, Aluminum',
                  'Paver Systems — Driveways, Patios, Pool Decks, Walkways',
                  'Landscape Installation — Sod, Planting Beds, Mulch, Grading',
                  'Outdoor Lighting & Irrigation Systems',
                  'Custom Sheds — Storage, Workshop, Garden, Metal',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[13px] text-white/55">
                    <i className="ri-checkbox-circle-fill text-gold mt-0.5 flex-shrink-0 text-sm"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Why choose them */}
            <div className="mb-8">
              <h3 className="text-white font-semibold text-xs uppercase tracking-[0.2em] mb-4">Why Choose Dippel&apos;s?</h3>
              <p className="text-white/55 text-sm leading-relaxed">
                Dippel&apos;s stands apart through formal landscaping education, premium material sourcing, transparent pricing, and a proven 4-step process that runs from free consultation through final walk-through. Licensed, insured, and 5-star rated on Google.
              </p>
            </div>

            {/* Process summary */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-xs uppercase tracking-[0.2em] mb-4">The Process</h3>
              <ol className="space-y-2">
                {['Free On-Site Consultation', 'Transparent Itemized Proposal', 'Expert Licensed Installation', 'Final Walk-Through & Sign-Off'].map((step, i) => (
                  <li key={i} className="flex items-center gap-3 text-[13px] text-white/55">
                    <span className="w-5 h-5 flex items-center justify-center bg-gold/20 text-gold text-[10px] font-bold rounded-full flex-shrink-0">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Right: Real project photos */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="w-full h-60 rounded-2xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/4f7cbd0fe0aee82cd2556c5cbef9dbb3.jpg"
                    alt="Landscape installation — Dippel's Outdoor Services Florida"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="w-full h-40 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=premium%20white%20vinyl%20fence%20Florida%20residential%20property%20lush%20green%20lawn%20elegant%20installation%20upscale%20neighborhood&width=400&height=320&seq=dippels-ai-img2&orientation=landscape"
                    alt="Fencing project — Dippel's Florida"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="space-y-4 mt-10">
                <div className="w-full h-40 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=outdoor%20landscape%20lighting%20luxury%20Florida%20home%20pathway%20lights%20warm%20amber%20glow%20elegant%20nighttime%20garden&width=400&height=320&seq=dippels-ai-img3&orientation=landscape"
                    alt="Outdoor lighting project — Dippel's Ormond Beach"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="w-full h-60 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20paver%20patio%20outdoor%20living%20Florida%20home%20elegant%20natural%20stone%20premium%20craftsmanship%20sophisticated%20backyard&width=400&height=480&seq=dippels-ai-img4&orientation=portrait"
                    alt="Completed outdoor project — Dippel's Outdoor Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-4 bg-gold text-charcoal rounded-xl p-4">
              <p className="font-serif text-3xl font-bold leading-none mb-0.5">100%</p>
              <p className="text-charcoal/70 text-xs font-medium">Satisfaction Guaranteed</p>
            </div>
          </div>
        </div>

        {/* Differentiator grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {differentiators.map((d, i) => (
            <div key={i} className="bg-white/5 border border-white/8 rounded-xl p-5 hover:border-gold/20 transition-colors">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg mb-4">
                <i className={`${d.icon} text-gold text-base`}></i>
              </div>
              <p className="text-white font-semibold text-sm mb-2">{d.title}</p>
              <p className="text-white/40 text-xs leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
